/**
 * Suilend (Bluefin Lend) takip ekranı — /suilend
 *
 * İki veri kaynağı bir arada:
 *   • CANLI pozisyon (`get_suilend_live`) — zincirden o an okunur. Snapshot saatlik
 *     olduğu için sayfa açılışında bir saat eski rakam göstermemek adına gerekli.
 *   • SNAPSHOT geçmişi + OLAY defteri (`get_suilend_snapshots`) — seri ve ayrıştırma.
 *
 * Navi ekranıyla aynı ayrıştırma mantığı: "ölçek" kullanıcı eylemini, "endeks" faizi
 * taşır; ikisi ayrı geldiği için iki snapshot arası fark faiz/kullanıcı diye bölünebilir.
 * Suilend çok piyasalı olduğundan her şey piyasa başına gruplanıyor.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RefreshCw, ArrowLeft, Wallet, TrendingUp, TrendingDown, Gift, ShieldAlert, Clock, Landmark, CalendarDays,
} from 'lucide-react';
import { useWS } from '../WSContext';

// ── Tipler (backend ile eşleşir) ──────────────────────────────────────────────
interface Reward { coinType: string; symbol: string; decimals: number; raw: string }
interface Asset {
  symbol: string; coinType: string; decimals: number;
  supply: number; borrow: number;
  priceUsd: number | null; supplyUsd: number | null; borrowUsd: number | null;
  // ölçek + endeks AYRI: faiz/kullanıcı ayrışması bunlarla yapılır (string = taşma yok)
  ctoken: string; ctokenRatio: string;
  borrowScaled: string; borrowIndex: string;
  openLtv: number; closeLtv: number; borrowWeightBps: number;
  borrowApr: number; supplyApr: number;
  rewards: Reward[];
}
interface Market {
  marketId: string; marketAd: string; obligationId: string;
  assets: Asset[];
  supplyUsd: number; borrowUsd: number; weightedBorrowUsd: number;
  allowedUsd: number; unhealthyUsd: number; netUsd: number; utilizationPct: number;
}
interface Snapshot { ts: number; markets: Market[]; supplyUsd: number; borrowUsd: number; netUsd: number }
interface LedgerRow {
  txHash: string; eventSeq: number; ts: number; marketId: string;
  kind: string; symbol: string; amount: number; ctoken: number; fee: number;
}
/** Canlı okuma backend'de ham gelir (miktarlar string + ondalık ayrı). */
interface LiveAsset extends Omit<Asset, 'supply' | 'borrow' | 'supplyUsd' | 'borrowUsd'> {
  supply: string; borrow: string;
}
interface LiveMarket {
  marketId: string; marketAd: string; obligationId: string; assets: LiveAsset[];
  cachedDepositUsd: number; cachedBorrowUsd: number; cachedUnhealthyUsd: number; badDebtUsd: number;
}

// ── Yardımcılar ───────────────────────────────────────────────────────────────
const fmt = (n: number, d = 4) =>
  (n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d });
const fmtUsd = (n: number) =>
  '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSigned = (n: number, d = 4) => (n > 0 ? '+' : '') + fmt(n, d);
const fmtDate = (ms: number) =>
  ms ? new Date(ms).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const shortId = (s: string) => (s ? `${s.slice(0, 6)}…${s.slice(-4)}` : '');
const pow10 = (d: number) => Math.pow(10, d || 0);

const WAD = BigInt('1000000000000000000');          // 1e18 (es5 hedefi: 10n / ** yok)
const ZERO = BigInt(0);

/** Canlı okumayı snapshot biçimine indirger → tek bir render yolu kullanılır. */
function liveToSnapshot(markets: LiveMarket[], ts: number): Snapshot | null {
  if (!markets?.length) return null;
  const mkts: Market[] = markets.map(m => {
    const assets: Asset[] = m.assets.map(a => {
      const d = pow10(a.decimals);
      const sup = Number(a.supply || '0') / d;
      const bor = Number(a.borrow || '0') / d;
      const p = a.priceUsd;
      return {
        ...a, supply: sup, borrow: bor,
        supplyUsd: p ? sup * p : null, borrowUsd: p ? bor * p : null,
      };
    });
    const supplyUsd = assets.reduce((s, a) => s + (a.supplyUsd || 0), 0);
    const borrowUsd = assets.reduce((s, a) => s + (a.borrowUsd || 0), 0);
    const weighted  = assets.reduce((s, a) => s + (a.borrowUsd || 0) * (a.borrowWeightBps || 10000) / 10000, 0);
    const allowed   = assets.reduce((s, a) => s + (a.supplyUsd || 0) * (a.openLtv || 0) / 100, 0);
    const unhealthy = assets.reduce((s, a) => s + (a.supplyUsd || 0) * (a.closeLtv || 0) / 100, 0);
    return {
      marketId: m.marketId, marketAd: m.marketAd, obligationId: m.obligationId,
      assets: assets.slice().sort((x, y) =>
        ((y.supplyUsd || 0) + (y.borrowUsd || 0)) - ((x.supplyUsd || 0) + (x.borrowUsd || 0))),
      supplyUsd, borrowUsd, weightedBorrowUsd: weighted,
      allowedUsd: allowed, unhealthyUsd: unhealthy,
      netUsd: supplyUsd - borrowUsd,
      utilizationPct: unhealthy > 0 ? (weighted / unhealthy) * 100 : 0,
    };
  }).sort((a, b) => (b.supplyUsd + b.borrowUsd) - (a.supplyUsd + a.borrowUsd));
  return {
    ts, markets: mkts,
    supplyUsd: mkts.reduce((s, m) => s + m.supplyUsd, 0),
    borrowUsd: mkts.reduce((s, m) => s + m.borrowUsd, 0),
    netUsd: mkts.reduce((s, m) => s + m.netUsd, 0),
  };
}

/** İki snapshot arası değişimi FAİZ ve KULLANICI HAREKETİ olarak ayrıştırır.
 *  Suilend muhasebesi: gerçek = ölçek × endeks / 1e18.
 *   • ölçek sabit  → değişimin tamamı faiz
 *   • ölçek değişti → aradaki fark kullanıcı işlemi
 *  Faiz = eski ölçeğin YENİ endeksle değeri − eski gerçek değer. */
interface Decomp { market: string; symbol: string; interest: number; user: number; kind: 'supply' | 'borrow' }
function decompose(first: Snapshot, last: Snapshot): Decomp[] {
  const out: Decomp[] = [];
  for (const m of last.markets) {
    const fm = first.markets.find(x => x.marketId === m.marketId);
    if (!fm) continue;
    for (const a of m.assets) {
      const f = fm.assets.find(x => x.coinType === a.coinType);
      if (!f) continue;
      const d = pow10(a.decimals);
      for (const kind of ['supply', 'borrow'] as const) {
        const sup = kind === 'supply';
        const scOld = BigInt((sup ? f.ctoken : f.borrowScaled) || '0');
        const scNew = BigInt((sup ? a.ctoken : a.borrowScaled) || '0');
        const ixOld = BigInt((sup ? f.ctokenRatio : f.borrowIndex) || '0');
        const ixNew = BigInt((sup ? a.ctokenRatio : a.borrowIndex) || '0');
        if ((scOld === ZERO && scNew === ZERO) || ixOld === ZERO || ixNew === ZERO) continue;
        const oldReal = (scOld * ixOld) / WAD;
        const midReal = (scOld * ixNew) / WAD;   // eski ölçek + yeni endeks = saf faiz
        const newReal = (scNew * ixNew) / WAD;
        const interest = Number(midReal - oldReal) / d;
        const user = Number(newReal - midReal) / d;
        if (Math.abs(interest) < 1e-9 && Math.abs(user) < 1e-9) continue;
        out.push({ market: m.marketAd, symbol: a.symbol, interest, user, kind });
      }
    }
  }
  return out.sort((a, b) => Math.abs(b.interest) - Math.abs(a.interest));
}

// ── Basit SVG çizgi grafiği (harici bağımlılık yok) ───────────────────────────
function Sparkline({ points, color = '#38bdf8', height = 120 }:
  { points: { ts: number; v: number }[]; color?: string; height?: number }) {
  if (points.length < 2) return null;
  const W = 100, H = 100;
  const vs = points.map(p => p.v);
  const min = Math.min(...vs), max = Math.max(...vs);
  const span = max - min || Math.abs(max) || 1;
  const pad = span * 0.12;
  const lo = min - pad, hi = max + pad;
  const x = (i: number) => (i / (points.length - 1)) * W;
  const y = (v: number) => H - ((v - lo) / (hi - lo)) * H;
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(2)},${y(p.v).toFixed(2)}`).join(' ');
  return (
    <div style={{ height }} className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id="slfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${d} L${W},${H} L0,${H} Z`} fill="url(#slfill)" />
        <path d={d} fill="none" stroke={color} strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

const ISLEM_ETIKET: Record<string, string> = {
  deposit: 'Yatırma', withdraw: 'Çekme', borrow: 'Borçlanma', repay: 'Geri ödeme',
  claim_reward: 'Ödül alma', liquidate: 'Likidasyon', forgive: 'Silinen borç',
};
const ISLEM_RENK: Record<string, string> = {
  deposit: 'text-emerald-400', withdraw: 'text-amber-400',
  borrow: 'text-rose-400', repay: 'text-sky-400',
  claim_reward: 'text-violet-400', liquidate: 'text-red-500', forgive: 'text-slate-400',
};

interface Props { dark?: boolean }

export default function SuilendTrax({ dark = true }: Props) {
  const { sendWs, subscribe, connected } = useWS() as any;

  const [snaps, setSnaps] = useState<Snapshot[]>([]);
  const [events, setEvents] = useState<LedgerRow[]>([]);
  const [live, setLive] = useState<Snapshot | null>(null);
  const [liveErr, setLiveErr] = useState('');
  const [wallet, setWallet] = useState('');
  const [wallets, setWallets] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [sekme, setSekme] = useState<'pozisyon' | 'gecmis'>('pozisyon');

  const load = useCallback((w?: string) => {
    setLoading(true);
    setLiveErr('');
    sendWs?.({ type: 'get_suilend_snapshots', payload: w ? { wallet: w } : {} });
    sendWs?.({ type: 'get_suilend_live', payload: w ? { wallet: w } : {} });
  }, [sendWs]);

  useEffect(() => { if (connected) load(); }, [connected, load]);

  useEffect(() => {
    const unsub = subscribe?.((msg: any) => {
      if (msg.type === 'suilend_snapshots') {
        setLoading(false);
        setSnaps(msg.payload?.snapshots ?? []);
        setEvents(msg.payload?.events ?? []);
        setWallet(msg.payload?.wallet ?? '');
        setWallets(msg.payload?.wallets ?? []);
      } else if (msg.type === 'suilend_live') {
        setLiveErr(msg.payload?.error || '');
        setLive(liveToSnapshot(msg.payload?.markets ?? [], Date.now()));
      }
    });
    return () => unsub?.();
  }, [subscribe]);

  // ── Tema (Navi ekranıyla aynı dil) ──────────────────────────────────────────
  const bg    = dark ? 'bg-[#03112b]' : 'bg-[#f4f7fb]';
  const card  = dark ? 'bg-[#061d3f] border-[#0a2560]' : 'bg-white border-[#d4e0f0]';
  const sub   = dark ? 'bg-[#03112b]/60 border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]';
  const text  = dark ? 'text-blue-50' : 'text-slate-800';
  const muted = dark ? 'text-blue-300/70' : 'text-slate-500';
  const satir = dark ? 'border-[#0a2560]' : 'border-[#e3ebf5]';

  // Gösterilecek pozisyon: canlı varsa o, yoksa son snapshot (sessizce boş kalmasın)
  const sonSnap = snaps.length ? snaps[snaps.length - 1] : null;
  const gosterilen = live ?? sonSnap;
  const canli = !!live;

  const series = useMemo(() => snaps.map(s => ({ ts: s.ts, v: s.netUsd })), [snaps]);
  const first = snaps.length ? snaps[0] : null;
  const decomp = useMemo(
    () => (first && sonSnap && first !== sonSnap ? decompose(first, sonSnap) : []),
    [first, sonSnap]);
  const netChange = first && sonSnap ? sonSnap.netUsd - first.netUsd : 0;

  const toplamOdul = useMemo(() => {
    const m: Record<string, { symbol: string; miktar: number }> = {};
    for (const mk of gosterilen?.markets ?? [])
      for (const a of mk.assets)
        for (const r of a.rewards ?? []) {
          const k = r.coinType || r.symbol;
          const v = Number(r.raw || '0') / pow10(r.decimals);
          if (!v) continue;
          m[k] = { symbol: r.symbol, miktar: (m[k]?.miktar || 0) + v };
        }
    return Object.values(m).sort((a, b) => b.miktar - a.miktar);
  }, [gosterilen]);

  return (
    <div className={`min-h-screen ${bg} ${text} p-4 md:p-6`}>
      <div className="max-w-6xl mx-auto">
        {/* Başlık */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button onClick={() => window.location.assign('/lptrax')}
            className={`p-2 rounded-lg border ${sub} ${muted} hover:text-sky-400`} title="LP Tracker'a dön">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <Wallet size={18} className="text-sky-400" /> Suilend Takip
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${sub} ${muted} font-normal`}>Bluefin Lend</span>
            </h1>
            <p className={`text-[11px] ${muted}`}>
              Lending / borrowing · {canli ? 'canlı zincir okuması' : 'son snapshot'}
              {wallet && <>· <span className="font-mono">{shortId(wallet)}</span></>}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {wallets.length > 1 && (
              <select value={wallet} onChange={e => { setLive(null); load(e.target.value); }}
                className={`px-2 py-1.5 rounded-lg border text-xs font-mono ${sub} ${muted}`}>
                {wallets.map(w => <option key={w} value={w}>{shortId(w)}</option>)}
              </select>
            )}
            <button onClick={() => window.location.assign('/navi')} title="Navi takibine geç"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs ${sub} ${muted} hover:text-sky-400`}>
              <Landmark size={13} /> Navi
            </button>
            <button onClick={() => window.location.assign('/butce')} title="Bütçe — kişisel gelir/gider defteri"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs ${sub} ${muted} hover:text-violet-300`}>
              <CalendarDays size={13} /> Bütçe
            </button>
            <button onClick={() => load(wallet)} disabled={loading}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs ${sub} ${muted} hover:text-sky-400 disabled:opacity-50`}>
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Yenile
            </button>
          </div>
        </div>

        {liveErr && (
          <div className={`rounded-lg border ${sub} px-3 py-2 mb-3 text-[11px] text-amber-400 flex items-center gap-2`}>
            <ShieldAlert size={13} />
            Canlı okuma başarısız, son snapshot gösteriliyor — {liveErr}
          </div>
        )}

        {!gosterilen && (
          <p className={`text-sm ${muted} py-10 text-center`}>
            {loading ? 'Yükleniyor…' : 'Bu cüzdanda Suilend pozisyonu bulunamadı.'}
          </p>
        )}

        {gosterilen && (
          <>
            {/* Özet */}
            <div className={`rounded-xl border ${card} p-3 mb-3 grid grid-cols-2 md:grid-cols-4 gap-3`}>
              <div>
                <div className={`text-[11px] ${muted}`}>Teminat</div>
                <div className="text-lg font-semibold leading-tight text-emerald-400">{fmtUsd(gosterilen.supplyUsd)}</div>
              </div>
              <div>
                <div className={`text-[11px] ${muted}`}>Borç</div>
                <div className="text-lg font-semibold leading-tight text-rose-400">{fmtUsd(gosterilen.borrowUsd)}</div>
              </div>
              <div>
                <div className={`text-[11px] ${muted}`}>Net değer</div>
                <div className="text-lg font-semibold leading-tight text-sky-400">{fmtUsd(gosterilen.netUsd)}</div>
              </div>
              <div>
                <div className={`text-[11px] ${muted} flex items-center gap-1`}>
                  <Clock size={11} /> {canli ? 'Şimdi' : fmtDate(gosterilen.ts)}
                </div>
                <div className={`text-lg font-semibold leading-tight ${netChange >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {snaps.length > 1 ? fmtSigned(netChange, 2) : '—'}
                  <span className={`text-[10px] ml-1 ${muted}`}>{snaps.length > 1 ? 'USD / dönem' : 'tek snapshot'}</span>
                </div>
              </div>
            </div>

            {/* Sekmeler */}
            <div className="flex items-center gap-1 mb-3">
              {([['pozisyon', 'Pozisyon'], ['gecmis', `İşlem geçmişi (${events.length})`]] as const).map(([k, l]) => (
                <button key={k} onClick={() => setSekme(k as any)}
                  className={`px-3 py-1.5 rounded-lg border text-xs ${sekme === k
                    ? 'border-sky-500/60 text-sky-300 bg-sky-500/10'
                    : `${sub} ${muted} hover:text-sky-400`}`}>{l}</button>
              ))}
            </div>

            {sekme === 'pozisyon' && (
              <>
                {/* Piyasa kartları — varlık satırı olmayan piyasa GÖSTERİLMEZ.
                    (Kullanıcının obligation'ı var ama içi boş olabiliyor: boş bir
                    tablo "bozuk" görünürdü. Hepsi boşsa aşağıda not çıkıyor.) */}
                {gosterilen.markets.filter(m => m.assets.length > 0).length === 0 && (
                  <p className={`text-[11px] ${muted} py-6 text-center`}>
                    {gosterilen.markets.length
                      ? `${gosterilen.markets.length} piyasada hesap açık ama içi boş — teminat/borç yok.`
                      : 'Açık pozisyon yok.'}
                  </p>
                )}
                {gosterilen.markets.filter(m => m.assets.length > 0).map(m => {
                  const kullanim = Math.max(0, Math.min(m.utilizationPct, 100));
                  const renk = kullanim >= 85 ? 'bg-rose-500' : kullanim >= 60 ? 'bg-amber-500' : 'bg-emerald-500';
                  return (
                    <div key={m.marketId} className={`rounded-xl border ${card} p-3 mb-3`}>
                      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                        <h2 className="text-sm font-semibold">{m.marketAd}</h2>
                        <span className={`text-[10px] font-mono ${muted}`}>{shortId(m.obligationId)}</span>
                        <div className="ml-auto flex items-center gap-3 text-[11px]">
                          <span className="text-emerald-400">{fmtUsd(m.supplyUsd)}</span>
                          <span className={muted}>/</span>
                          <span className="text-rose-400">{fmtUsd(m.borrowUsd)}</span>
                        </div>
                      </div>

                      {/* Likidasyon mesafesi — eşiğe göre doluluk */}
                      {m.unhealthyUsd > 0 && (
                        <div className="mb-3">
                          <div className={`flex justify-between text-[10px] ${muted} mb-1`}>
                            <span>Likidasyon eşiğine doluluk</span>
                            <span>
                              {fmt(kullanim, 1)}% · borç {fmtUsd(m.weightedBorrowUsd)} / eşik {fmtUsd(m.unhealthyUsd)}
                              <span className="ml-1 opacity-70">(izin {fmtUsd(m.allowedUsd)})</span>
                            </span>
                          </div>
                          <div className={`h-1.5 rounded-full overflow-hidden ${dark ? 'bg-[#03112b]' : 'bg-slate-200'}`}>
                            <div className={`h-full ${renk}`} style={{ width: `${kullanim}%` }} />
                          </div>
                        </div>
                      )}

                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead className={`${muted} text-left`}>
                            <tr className={`border-b ${satir}`}>
                              <th className="py-1.5 pr-2 font-medium">Varlık</th>
                              <th className="py-1.5 px-2 font-medium text-right">Teminat</th>
                              <th className="py-1.5 px-2 font-medium text-right">Borç</th>
                              <th className="py-1.5 px-2 font-medium text-right">Fiyat</th>
                              <th className="py-1.5 px-2 font-medium text-right">APR arz/borç</th>
                              <th className="py-1.5 px-2 font-medium text-right">LTV / eşik</th>
                              <th className="py-1.5 pl-2 font-medium">Toplanmamış ödül</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.assets.map(a => (
                              <tr key={a.coinType} className={`border-b ${satir} last:border-0`}>
                                <td className="py-1.5 pr-2 font-medium">{a.symbol}</td>
                                <td className="py-1.5 px-2 text-right">
                                  {a.supply ? <>
                                    <span className="text-emerald-400">{fmt(a.supply, 6)}</span>
                                    <span className={`block text-[10px] ${muted}`}>{fmtUsd(a.supplyUsd || 0)}</span>
                                  </> : <span className={muted}>—</span>}
                                </td>
                                <td className="py-1.5 px-2 text-right">
                                  {a.borrow ? <>
                                    <span className="text-rose-400">{fmt(a.borrow, 8)}</span>
                                    <span className={`block text-[10px] ${muted}`}>{fmtUsd(a.borrowUsd || 0)}</span>
                                  </> : <span className={muted}>—</span>}
                                </td>
                                <td className={`py-1.5 px-2 text-right ${muted}`}>
                                  {a.priceUsd ? '$' + fmt(a.priceUsd, a.priceUsd > 100 ? 2 : 4) : '—'}
                                </td>
                                <td className="py-1.5 px-2 text-right whitespace-nowrap">
                                  <span className="text-emerald-400">{fmt(a.supplyApr, 2)}%</span>
                                  <span className={muted}> / </span>
                                  <span className="text-rose-400">{fmt(a.borrowApr, 2)}%</span>
                                </td>
                                <td className={`py-1.5 px-2 text-right ${muted} whitespace-nowrap`}>
                                  {a.openLtv}% / {a.closeLtv}%
                                </td>
                                <td className="py-1.5 pl-2">
                                  {(a.rewards ?? []).length ? (
                                    <span className="text-violet-400 flex items-center gap-1 flex-wrap">
                                      <Gift size={11} />
                                      {a.rewards.map((r, i) => (
                                        <span key={i} className="whitespace-nowrap">
                                          {fmt(Number(r.raw || '0') / pow10(r.decimals), 6)} {r.symbol}
                                        </span>
                                      ))}
                                    </span>
                                  ) : <span className={muted}>—</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}

                {/* Toplam ödül */}
                {toplamOdul.length > 0 && (
                  <div className={`rounded-xl border ${card} p-3 mb-3`}>
                    <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <Gift size={14} className="text-violet-400" /> Toplanmamış ödüller
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {toplamOdul.map((r, i) => (
                        <span key={i} className={`px-2 py-1 rounded-lg border text-[11px] ${sub}`}>
                          <span className="text-violet-300 font-medium">{fmt(r.miktar, 6)}</span>{' '}
                          <span className={muted}>{r.symbol}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Net değer serisi + faiz/kullanıcı ayrışması */}
                {snaps.length > 1 && (
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div className={`rounded-xl border ${card} p-3`}>
                      <h2 className="text-sm font-semibold mb-1">Net değer</h2>
                      <p className={`text-[10px] ${muted} mb-2`}>
                        {fmtDate(snaps[0].ts)} → {fmtDate(sonSnap!.ts)} · {snaps.length} snapshot
                      </p>
                      <Sparkline points={series} color={netChange >= 0 ? '#34d399' : '#fb7185'} />
                    </div>
                    <div className={`rounded-xl border ${card} p-3`}>
                      <h2 className="text-sm font-semibold mb-1">Faiz mi, işlem mi?</h2>
                      <p className={`text-[10px] ${muted} mb-2`}>
                        Ölçek sabit kalıp endeks değiştiyse fark FAİZ, ölçek değiştiyse KULLANICI işlemidir.
                      </p>
                      {decomp.length ? (
                        <table className="w-full text-[11px]">
                          <thead className={`${muted} text-left`}>
                            <tr className={`border-b ${satir}`}>
                              <th className="py-1 pr-2 font-medium">Varlık</th>
                              <th className="py-1 px-2 font-medium text-right">Faiz</th>
                              <th className="py-1 pl-2 font-medium text-right">İşlem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {decomp.slice(0, 8).map((d, i) => (
                              <tr key={i} className={`border-b ${satir} last:border-0`}>
                                <td className="py-1 pr-2">
                                  {d.symbol}
                                  <span className={`text-[10px] ml-1 ${d.kind === 'supply' ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    {d.kind === 'supply' ? 'teminat' : 'borç'}
                                  </span>
                                  <span className={`text-[10px] ml-1 ${muted}`}>{d.market}</span>
                                </td>
                                <td className={`py-1 px-2 text-right ${d.interest >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                  {fmtSigned(d.interest, 6)}
                                </td>
                                <td className={`py-1 pl-2 text-right ${muted}`}>{fmtSigned(d.user, 6)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : <p className={`text-[11px] ${muted}`}>Henüz ayrıştırılacak değişim yok.</p>}
                    </div>
                  </div>
                )}
              </>
            )}

            {sekme === 'gecmis' && (
              <div className={`rounded-xl border ${card} p-3`}>
                <h2 className="text-sm font-semibold mb-1">İşlem geçmişi</h2>
                <p className={`text-[10px] ${muted} mb-2`}>
                  Zincirdeki olaylardan türetildi — geriye dönük doldurulabilir, snapshot'a bağlı değil.
                  Mint/Redeem (mekanik yarı) birleştirildi.
                </p>
                {events.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead className={`${muted} text-left`}>
                        <tr className={`border-b ${satir}`}>
                          <th className="py-1.5 pr-2 font-medium">Tarih</th>
                          <th className="py-1.5 px-2 font-medium">İşlem</th>
                          <th className="py-1.5 px-2 font-medium">Varlık</th>
                          <th className="py-1.5 px-2 font-medium text-right">Miktar</th>
                          <th className="py-1.5 px-2 font-medium text-right">Komisyon</th>
                          <th className="py-1.5 pl-2 font-medium">Tx</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((e, i) => (
                          <tr key={`${e.txHash}-${e.eventSeq}-${i}`} className={`border-b ${satir} last:border-0`}>
                            <td className={`py-1.5 pr-2 ${muted} whitespace-nowrap`}>{fmtDate(e.ts)}</td>
                            <td className={`py-1.5 px-2 font-medium whitespace-nowrap ${ISLEM_RENK[e.kind] || ''}`}>
                              {e.kind === 'deposit' ? <TrendingUp size={11} className="inline mr-1" />
                                : e.kind === 'withdraw' ? <TrendingDown size={11} className="inline mr-1" /> : null}
                              {ISLEM_ETIKET[e.kind] || e.kind}
                            </td>
                            <td className="py-1.5 px-2">{e.symbol || '—'}</td>
                            <td className="py-1.5 px-2 text-right">{e.amount ? fmt(e.amount, 6) : <span className={muted}>—</span>}</td>
                            <td className={`py-1.5 px-2 text-right ${muted}`}>{e.fee ? fmt(e.fee, 6) : '—'}</td>
                            <td className="py-1.5 pl-2">
                              <a href={`https://suiscan.xyz/mainnet/tx/${e.txHash}`} target="_blank" rel="noreferrer"
                                className={`font-mono ${muted} hover:text-sky-400`}>{e.txHash.slice(0, 8)}…</a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className={`text-[11px] ${muted}`}>Henüz işlem kaydı yok.</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
